"use client";

import { useState } from "react";
import { formatCurrency } from "../../../utils/formatCurrency.js";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import useDeleteDoc from "../../../hooks/useDeleteDoc.js";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";

function ModalAbono({
  data,
  modalAbonos,
  setModalAbonos,
  requestId,
  requestData,
  investor,
}) {
  const abonos = Array.isArray(data) ? data : [];
  const [abonoToDelete, setAbonoToDelete] = useState(null);
  const [selectedAbono, setSelectedAbono] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { handleUpdate } = useUpdateDoc();
  const { handleDelete } = useDeleteDoc();

  const { data: abonosData, isFetching: loading } = useSnapshotWhere(
    "investorAbonos",
    {
      filter: {
        firstCondition: "creditId",
        secondCondition: "==",
        thirdCondition: requestId,
      },
    }
  );

  const handleDeleteAbono = async () => {
    setIsDeleting(true);

    try {
      const updatedAbonos = abonos.filter(
        (_, index) => index !== abonoToDelete
      );

      await handleUpdate("requests", requestId, {
        abono: updatedAbonos,
      });

      if (investor) {
        await handleUpdate("investor", investor.uuid, {
          currentAmmount:
            investor.currentAmmount - Number(selectedAbono?.amount || 0),
        });
      }
      const getMovementAbono = abonosData?.find(
        (el) => el.id === selectedAbono?.idAbono
      );

      if (getMovementAbono) {
        const res = await handleDelete(
          "investorMovements",
          getMovementAbono?.idMovement
        );
      }

      setShowDeleteConfirm(false);
      setAbonoToDelete(null);
    } catch (error) {
      console.error("Error eliminando abono:", error);
      alert("Error al eliminar el abono. Por favor intente nuevamente.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {modalAbonos && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="space-y-4">
              {/* Título */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  Detalles de Abonos
                </h3>
                <button
                  onClick={() => setModalAbonos(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Lista de abonos */}
              {abonos.length > 0 ? (
                <ul className="space-y-4">
                  {abonos.map((ab, i) => (
                    <li
                      key={i}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition relative"
                    >
                      {/* Botón eliminar */}
                      <button
                        onClick={() => {
                          setAbonoToDelete(i);
                          setSelectedAbono(ab);
                          setShowDeleteConfirm(true);
                        }}
                        className="absolute top-3 right-3 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar abono"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>

                      <div className="flex justify-between text-sm mb-2 pr-8">
                        <span className="text-gray-600">Fecha:</span>
                        <span className="font-medium text-gray-800">
                          {ab.date}
                        </span>
                      </div>

                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Monto:</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(ab.amount)}
                        </span>
                      </div>

                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Recalculo:</span>
                        <span className="font-medium capitalize">
                          {ab.type === "mantener"
                            ? "Mantener cuota"
                            : ab.type === "recalculo_plazo"
                            ? `Recalcular cuota, nuevo plazo: ${ab.remainingMonths}`
                            : "Recalcular cuota"}
                        </span>
                      </div>

                      {ab.detail && (
                        <div className="text-sm mb-2">
                          <span className="text-gray-600 block">Detalle:</span>
                          <p className="text-gray-800 mt-1 whitespace-pre-wrap">
                            {ab.detail}
                          </p>
                        </div>
                      )}

                      {ab.comprobante && (
                        <div className="text-sm">
                          <span className="text-gray-600 block">
                            Comprobante:
                          </span>
                          <a
                            href={ab.comprobante}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline mt-1 inline-block"
                          >
                            Ver archivo
                          </a>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay abonos registrados todavía.</p>
                </div>
              )}

              {/* Botón de cierre */}
              <div className="pt-4">
                <button
                  onClick={() => setModalAbonos(false)}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="space-y-4">
              {/* Ícono de advertencia */}
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>

              {/* Título y mensaje */}
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  ¿Eliminar Abono?
                </h3>
                <p className="text-gray-600 text-sm">
                  Esta acción no se puede deshacer. El abono será eliminado
                  permanentemente del registro.
                </p>
              </div>

              {/* Detalles del abono a eliminar */}
              {abonoToDelete !== null && abonos[abonoToDelete] && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fecha:</span>
                      <span className="font-medium">
                        {abonos[abonoToDelete].date}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monto:</span>
                      <span className="font-medium text-red-600">
                        {formatCurrency(abonos[abonoToDelete].amount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setAbonoToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteAbono}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Eliminando...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span>Eliminar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ModalAbono;
